import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { LogOut, LayoutDashboard, Sun, ListTodo, Users, SlidersHorizontal, Search, Newspaper } from "lucide-react";
import { TESTIDS } from "../../lib/testids";
import { useHotkeys } from "../../lib/useHotkeys";
import CommandPalette from "./CommandPalette";

const items = [
  { to: "/briefing", label: "Briefing du jour", icon: Sun, testid: TESTIDS.navBriefing },
  { to: "/dashboard", label: "Vue dirigeant", icon: LayoutDashboard, testid: TESTIDS.navDashboard },
  { to: "/brief-matin", label: "Brief matinal", icon: Newspaper, testid: "nav-brief-matin" },
  { to: "/propales", label: "Propales", icon: ListTodo, testid: TESTIDS.navPropales },
  { to: "/clients", label: "Clients", icon: Users, testid: TESTIDS.navClients },
  { to: "/reglages", label: "Réglages", icon: SlidersHorizontal, testid: TESTIDS.navSettings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const visibleItems = items;

  useHotkeys({
    "mod+k": () => setPaletteOpen((v) => !v),
  });

  return (
    <div className="th-scope min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: "var(--th-bg)" }}>
      <aside
        className="md:w-60 md:min-h-screen flex md:flex-col"
        style={{
          backgroundColor: "var(--th-surface)",
          borderRight: "1px solid var(--th-border)",
        }}
      >
        <div
          className="p-4 md:p-6 flex-1 md:flex-none"
          style={{ borderBottom: "1px solid var(--th-border)" }}
        >
          <div className="th-section-label">Pilote</div>
          <div
            className="font-th-serif text-xl leading-none mt-1"
            style={{ fontFamily: "var(--th-font-serif)", fontWeight: 500, color: "var(--th-text)" }}
          >
            Commercial
          </div>
        </div>

        <button
          data-testid="cmdk-open"
          onClick={() => setPaletteOpen(true)}
          className="hidden md:flex items-center gap-2 mx-3 mt-3 px-3 py-2 text-sm"
          style={{
            color: "var(--th-text-3)",
            border: "1px solid var(--th-border)",
            borderRadius: 8,
            backgroundColor: "var(--th-surface)",
          }}
        >
          <Search size={14} />
          <span>Rechercher…</span>
          <span
            className="ml-auto text-[10px] font-medium"
            style={{
              border: "1px solid var(--th-border-strong)",
              padding: "1px 6px",
              borderRadius: 4,
              color: "var(--th-text-2)",
            }}
          >
            ⌘K
          </span>
        </button>

        <nav className="flex md:flex-col md:p-3 md:mt-2 md:gap-1 overflow-x-auto md:overflow-visible">
          {visibleItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `th-nav-item ${isActive ? "is-active" : ""}`
                }
              >
                <Icon size={16} strokeWidth={1.75} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div
          className="hidden md:block mt-auto p-4 text-xs"
          style={{ borderTop: "1px solid var(--th-border)", color: "var(--th-text-3)" }}
        >
          {user && (
            <div className="mb-2 flex items-center gap-2 min-w-0">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  width={22}
                  height={22}
                  style={{ borderRadius: "50%", flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 22, height: 22, borderRadius: "50%",
                    backgroundColor: "var(--th-navy-tint)",
                    color: "var(--th-navy)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 600, flexShrink: 0,
                  }}
                >
                  {(user.name || user.username || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div
                  className="truncate"
                  style={{ color: "var(--th-text)", fontWeight: 500, fontSize: 12 }}
                  title={user.name || user.username}
                >
                  {user.name || user.username}
                </div>
                {user.email && (
                  <div className="truncate" style={{ fontSize: 10 }} title={user.email}>
                    {user.email}
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            data-testid={TESTIDS.logoutBtn}
            onClick={logout}
            className="flex items-center gap-2 hover:underline"
            style={{ color: "var(--th-text-2)" }}
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
